import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { TabData, TabsProps } from './Tabs.types';

export const useTabs = ({
    tabs,
    onChange,
    defaultTabId,
    shouldUpdateUrl,
    index,
    setIndex,
    isControlled = false,
}: TabsProps): {
    handleChange: (newIndex: number) => void;
    tabIndex: number;
    visibleTabs: TabData[];
} => {
    const visibleTabs = tabs.filter((tab: TabData) => tab.canView);
    const tabIds = visibleTabs.map((tab: TabData) => tab.id);

    const history = useHistory();
    const { pathname, search } = useLocation();

    const query = new URLSearchParams(search);
    const tabByURL = query.get('tab');

    const [tabIndex, setTabIndex] = useState<number>();

    const keyOfLegacyRoute = useMemo(() => {
        const pathnameParts = history.location.pathname.split('/');
        let legacyKey;

        let attempts = 0;
        while (!legacyKey && attempts < 2) {
            legacyKey = pathnameParts.pop();
            attempts += 1;
        }

        return legacyKey;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateURL = useCallback(
        (tabId: string) => {
            const searchParams = new URLSearchParams(history.location.search);

            searchParams.forEach((_, key) => {
                if (key !== 'tab') {
                    searchParams.delete(key);
                }
            });
            searchParams.set('tab', tabId);

            history.push({
                pathname,
                search: searchParams.toString(),
                hash: history.location.hash,
            });
        },
        [history, pathname]
    );

    const handleChange = useCallback(
        (newIndex: number) => {
            onChange?.(newIndex);
            if (shouldUpdateUrl) updateURL(tabIds[newIndex]);
            else {
                setIndex?.(newIndex);
                setTabIndex(newIndex);
            }
        },
        [onChange, shouldUpdateUrl, updateURL, tabIds, setIndex]
    );

    const getIndex = useCallback(
        (id: string) => {
            const idx = tabIds.indexOf(id);
            if (idx >= 0) {
                return idx;
            }

            const defaultIndex = tabIds.indexOf(defaultTabId ?? '');
            if (defaultIndex >= 0) {
                return defaultIndex;
            }

            return 0;
        },
        [defaultTabId, tabIds]
    );

    useEffect(() => {
        if (!shouldUpdateUrl) return;

        if (!tabByURL && keyOfLegacyRoute) {
            const idx = getIndex(keyOfLegacyRoute);
            setIndex?.(idx);
            setTabIndex(idx);
            return;
        }

        if (tabByURL && tabIds.includes(tabByURL)) {
            const idx = getIndex(tabByURL);
            setIndex?.(idx);
            setTabIndex(idx);
            return;
        }

        if (defaultTabId && tabIds.includes(defaultTabId)) {
            const idx = getIndex(defaultTabId);
            setIndex?.(idx);
            setTabIndex(idx);
            return;
        }

        setIndex?.(0);
        setTabIndex(0);
    }, [
        tabByURL,
        tabIds,
        defaultTabId,
        keyOfLegacyRoute,
        getIndex,
        shouldUpdateUrl,
        setIndex,
    ]);

    return {
        handleChange,
        tabIndex: (isControlled ? index : tabIndex) || 0,
        visibleTabs,
    };
};
